import LayoutTabs from "@/components/controls/LayoutTabs";
import { useEffect, useRef, useState } from "react";
import { UserData } from "@/entities/users/types";
import FocusTab from "./components/FocusTab";
import { tabsPropsType } from "@/components/controls/Tabs";
import { HomeTabs } from "@/entities/home/types";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import PerformanceTab from "./components/PerformanceTab";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import Spinner from "@/components/ui/spinner";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const Home: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();
  // `enabled` resolves async — gate the render on `loading` or the flag-OFF
  // branch paints first and flashes the old widget/tabs before switching.
  const { enabled: showMyTasksPage, loading: showMyTasksPageLoading } =
    useFeatureFlag(FeatureFlags.CRM_9035_MY_TASKS_PAGE);

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<HomeTabs>(() => {
    const tabParam = searchParams.get("tab");
    return Object.values(HomeTabs).includes(tabParam as HomeTabs)
      ? (tabParam as HomeTabs)
      : HomeTabs.PERFORMANCE;
  });

  const layoutTabsProps: tabsPropsType = {
    options: {
      [HomeTabs.PERFORMANCE]: t(`tabs.${HomeTabs.PERFORMANCE}`),
      [HomeTabs.FOCUS]: t(`tabs.${HomeTabs.FOCUS}`)
    },
    defaultValue: selectedTab
  };

  useEffect(() => {
    if (showMyTasksPage || showMyTasksPageLoading) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", selectedTab);
      return params;
    });
  }, [selectedTab, setSearchParams, showMyTasksPage, showMyTasksPageLoading]);

  const stickyRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const isStickyRef = useRef(isSticky);

  const handleTabChange = (value: string) => {
    setSelectedTab(value as HomeTabs);
  };

  useEffect(() => {
    if (showMyTasksPage || showMyTasksPageLoading) return;

    const handleScroll = () => {
      const scrollContainer = document.querySelector(".overflow-y-auto");
      if (!scrollContainer) return;

      const scrollTop = scrollContainer.scrollTop;
      const newIsSticky = scrollTop > 500;

      if (newIsSticky !== isStickyRef.current) {
        isStickyRef.current = newIsSticky;
        setIsSticky(newIsSticky);
      }
    };

    const scrollContainer = document.querySelector(".overflow-y-auto");
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [showMyTasksPage, showMyTasksPageLoading]);

  if (showMyTasksPageLoading) {
    return (
      <div
        className="flex h-full items-center justify-center"
        data-test="home-page-loading"
      >
        <Spinner size="lg" />
      </div>
    );
  }

  if (showMyTasksPage) {
    return (
      <div className="bg-surface-elevation0" data-test="home-page">
        <div data-test="home-performance-tab">
          <PerformanceTab userData={userData} isAE={isAE} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={stickyRef}
        data-test="home-header"
        className="sticky top-0 z-30 bg-surface-elevation0"
      >
        <div className="flex flex-row items-start justify-between h-full">
          <div className="grow-1" />
          <div data-test="home-tabs-container" className="grow-[1]">
            <LayoutTabs
              data-test="home-tabs"
              {...layoutTabsProps}
              valueSelected={handleTabChange}
              size="lg"
            />
          </div>
          <div className="grow-1" />
        </div>
      </div>
      <div className="bg-surface-elevation0" data-test="home-page">
        {selectedTab === "performance" ? (
          <PerformanceTab
            data-test="home-performance-tab"
            userData={userData}
            isAE={isAE}
          />
        ) : selectedTab === "focus" ? (
          <FocusTab data-test="home-focus-tab" />
        ) : null}
      </div>
    </>
  );
};

export default Home;
